import { useEffect, useMemo, useState } from 'react'
import { categories, recipeBySlug, recipes } from './data'
import { RecipeBookMark } from './components/RecipeBookMark'
import { RecipeCard } from './components/RecipeCard'
import { RecipePage } from './components/RecipePage'
import { ShoppingList } from './components/ShoppingList'
import { normalizeSearch } from './utils/format'
import { mergeShoppingItems } from './utils/shopping'
import { loadShoppingList, saveShoppingList } from './utils/storage'
import type { RecipeCategory, ShoppingItem } from './types'

type Filter = 'toutes' | RecipeCategory
type Route = { view: 'home' | 'recipe' | 'shopping'; slug?: string }

const routeFromLocation = (): Route => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/liste-de-courses') return { view: 'shopping' }
  const recipeMatch = path.match(/^\/recettes\/([^/]+)$/)
  if (recipeMatch && recipeBySlug.has(recipeMatch[1])) return { view: 'recipe', slug: recipeMatch[1] }
  return { view: 'home' }
}

function App() {
  const [route, setRoute] = useState<Route>(routeFromLocation)
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>(loadShoppingList)
  const [toast, setToast] = useState('')

  useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => saveShoppingList(shoppingItems), [shoppingItems])

  // B8: the tab title never changed across routes, making tabs and bookmarks indistinguishable.
  useEffect(() => {
    if (route.view === 'shopping') {
      document.title = 'Liste de courses — Carnet de recettes'
    } else if (route.view === 'recipe' && route.slug) {
      const recipe = recipeBySlug.get(route.slug)
      document.title = recipe ? `${recipe.title} — Carnet de recettes` : 'Carnet de recettes'
    } else {
      document.title = 'Carnet de recettes'
    }
  }, [route])

  const navigate = (path: string) => {
    window.history.pushState({}, '', path)
    setRoute(routeFromLocation())
    // C4: respect prefers-reduced-motion — CSS scroll-behavior is overridden below, but a
    // JS-driven scrollTo's own `behavior` option ignores that and needs its own check.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
  }

  const addShoppingItems = (items: ShoppingItem[]) => {
    setShoppingItems((current) => mergeShoppingItems(current, items))
    setToast(`${items.length > 1 ? 'Ingrédients ajoutés' : 'Ingrédient ajouté'} à la liste`)
    window.setTimeout(() => setToast(''), 2400)
  }

  const currentRecipe = route.view === 'recipe' && route.slug ? recipeBySlug.get(route.slug) : undefined

  return (
    <div className="app-shell">
      <SiteHeader
        route={route}
        shoppingCount={shoppingItems.filter((item) => !item.checked).length}
        onNavigate={navigate}
      />
      {route.view === 'shopping' ? (
        <ShoppingList items={shoppingItems} onBack={() => navigate('/')} onChange={setShoppingItems} />
      ) : currentRecipe ? (
        <RecipePage
          key={currentRecipe.slug}
          onAdd={addShoppingItems}
          onBack={() => navigate('/')}
          recipe={currentRecipe}
        />
      ) : (
        <Home
          onOpen={(slug) => navigate(`/recettes/${slug}`)}
          onShopping={() => navigate('/liste-de-courses')}
        />
      )}
      {toast && (
        <div aria-live="polite" className="toast">
          {toast}
          <button aria-label="Fermer" onClick={() => setToast('')} type="button">
            ×
          </button>
        </div>
      )}
    </div>
  )
}

function SiteHeader({
  route,
  shoppingCount,
  onNavigate,
}: {
  route: Route
  shoppingCount: number
  onNavigate: (path: string) => void
}) {
  return (
    <header className="site-header">
      <a
        className="brand"
        href="/"
        onClick={(event) => {
          event.preventDefault()
          onNavigate('/')
        }}
      >
        <RecipeBookMark />
        <span>Carnet de recettes</span>
      </a>
      <nav className="site-nav" aria-label="Navigation principale">
        <a
          aria-current={route.view === 'home' ? 'page' : undefined}
          className={route.view === 'home' ? 'active' : ''}
          href="/"
          onClick={(event) => {
            event.preventDefault()
            onNavigate('/')
          }}
        >
          Recettes
        </a>
        <a
          aria-current={route.view === 'shopping' ? 'page' : undefined}
          className={route.view === 'shopping' ? 'active shopping-nav' : 'shopping-nav'}
          href="/liste-de-courses"
          onClick={(event) => {
            event.preventDefault()
            onNavigate('/liste-de-courses')
          }}
        >
          Liste de courses <span className="nav-count">{shoppingCount}</span>
        </a>
      </nav>
    </header>
  )
}

function Home({ onOpen, onShopping }: { onOpen: (slug: string) => void; onShopping: () => void }) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('toutes')
  const filtered = useMemo(
    () =>
      recipes.filter((recipe) => {
        const matchesCategory = filter === 'toutes' || recipe.category === filter
        // Recherche insensible aux accents : « crepes » doit trouver « Crêpes », « pates »
        // trouver « Pâtes aux châtaignes ». On normalise la requête et le texte cherché.
        const search = normalizeSearch(query)
        return (
          matchesCategory &&
          (!search ||
            normalizeSearch(
              `${recipe.title} ${recipe.description} ${recipe.tags.join(' ')}`,
            ).includes(search))
        )
      }),
    [filter, query],
  )

  return (
    <main className="page-main home-page">
      <div className="page-intro compact-intro catalogue-intro">
        <h1 id="page-title">Toutes les recettes</h1>
      </div>
      <section className="catalogue" aria-labelledby="page-title">
        <div className="catalogue-tools">
          <div className="catalogue-tools-main">
            <label className="search-field">
              <span aria-hidden="true">⌕</span>
              <input
                aria-label="Rechercher une recette"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher une recette"
                type="search"
                value={query}
              />
              {query && (
                <button aria-label="Effacer la recherche" onClick={() => setQuery('')} type="button">
                  ×
                </button>
              )}
            </label>
            <div className="filter-tabs" role="group" aria-label="Filtrer par catégorie">
              {[{ id: 'toutes' as const, label: 'Toutes' }, ...categories].map((category) => (
                <button
                  className={filter === category.id ? 'active' : ''}
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  type="button"
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
          <button className="list-pill" onClick={onShopping} type="button">
            <span className="list-pill-icon" aria-hidden="true">
              ☷
            </span>{' '}
            Ma liste de courses <span aria-hidden="true">→</span>
          </button>
        </div>
        {filtered.length === 0 ? (
          <div className="empty-search">
            <span aria-hidden="true">⌕</span>
            <h3>Aucune recette trouvée</h3>
            <p>Essaie un autre mot ou enlève le filtre.</p>
          </div>
        ) : (
          <div className="recipe-groups">
            {categories
              .filter((category) => filter === 'toutes' || filter === category.id)
              .map((category) => {
                const group = filtered.filter((recipe) => recipe.category === category.id)
                return group.length ? (
                  <section
                    className="recipe-group"
                    key={category.id}
                    aria-labelledby={`category-${category.id}`}
                  >
                    <div className="group-title">
                      <h3 id={`category-${category.id}`}>{category.label}</h3>
                      <span>{group.length}</span>
                    </div>
                    <div className="recipe-grid">
                      {group.map((recipe) => (
                        <RecipeCard key={recipe.slug} onOpen={onOpen} recipe={recipe} />
                      ))}
                    </div>
                  </section>
                ) : null
              })}
          </div>
        )}
      </section>
    </main>
  )
}

export default App
